import cep from './CatalogExplorerPage.module.scss';
import WithMainContainer from '@/components/layout';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { Flex, Typography } from 'antd';
import CatalogDisplay from '@/components/Catalog/CatalogDisplay/CatalogDisplay';
import CatalogExplorerCategory from '@/components/Catalog/CatalogExplorerCategory/CatalogExplorerCategory';
import { IBreadcrumb, ICatalogQueryParams } from 'tkmp-lib/dist/interfaces';
import { CatalogQueryService } from '@/utils/services/CatalogQueryService';
import CatalogBrandHeader from '@/components/Catalog/CatalogBrandHeader/CatalogBrandHeader';
import Head from 'next/head';
import { useStores } from '@/utils/hooks/useStores';
import { QueryService } from 'tkmp-lib/dist/services/QueryService';
import { AuthService } from 'tkmp-lib/dist/services/AuthService';
import useLocalization from 'tkmp-lib/dist/hooks/useLocalization';
import CatalogFiltersBar from '@/components/Catalog/CatalogFiltersBar/CatalogFiltersBar';
import CatalogActionsHeader from '@/components/Catalog/CatalogActionsHeader/CatalogActionsHeader';
import CatalogFiltersTags from '@/components/Catalog/CatalogFiltersTags/CatalogFiltersTags';
import HeadService from '@/utils/services/HeadService';

export async function getServerSideProps(context: any) {
    const { params } = context;
    const { catalog_query } = params;
    const cookies = context?.req?.cookies;
    // На случай если AuthService.validateAuthSSR(cookies) не будет работать
    const isAuth = await AuthService.checkRefreshSSR(context);
    const locale = QueryService.getLocale(context);

    const catalogType: ICatalogQueryParams =
        CatalogQueryService.generateCatalogQuery(catalog_query);

    const pageData = await QueryService.request(
        `${process.env.NEXT_PUBLIC_DOMAIN_BFF}/tkmp/v1/${locale}/catalog/category/home/${catalogType?.query_params?.join('/')}`,
    );

    if (catalogType) {
        let breadcrumbs: IBreadcrumb[] = [
            {
                key: '/',
                title: 'Главная',
            },
            {
                key: '/catalog',
                title: 'Каталог',
            },
        ];

        const {
            dto1: initialCatalogItems = null,
            dto2: filterItems = null,
            dto3: navResponse = null,
        } = await QueryService.requests(
            `${process.env.NEXT_PUBLIC_DOMAIN_BFF}/tkmp/v1/${locale}/catalog/product/home/${catalogType?.query_params?.join('/')}${CatalogQueryService.getFilterUrl(catalogType)}`,
            `${process.env.NEXT_PUBLIC_DOMAIN_BFF}/tkmp/v1/${locale}/catalog/filter/home/${catalogType?.query_params?.join('/')}`,
            `${process.env.NEXT_PUBLIC_DOMAIN_BFF}/tkmp/v1/${locale}/catalog/category/home/${catalogType?.query_params?.join('/')}`,
        );

        let catalogCategories: any = [];
        if (navResponse) {
            catalogCategories = catalogType.query_params.length !== 0 ? navResponse : [];

            breadcrumbs = [
                ...breadcrumbs,
                ...navResponse?.fpathSlug
                    ?.slice(1)
                    ?.reduce((acc: any, current: any, index: any) => {
                        const path =
                            acc.length > 0
                                ? `${acc[acc.length - 1].key}/${current}`
                                : `${breadcrumbs.at(-1)?.key}/${current}`;
                        return [...acc, { key: path, title: navResponse.fpathName[index + 1] }];
                    }, []),
            ];
        }

        const brandDto =
            catalogType?.isBrand && catalogType?.brand_name !== ''
                ? await QueryService.request(
                      `${process.env.NEXT_PUBLIC_DOMAIN_BFF}/tkmp/v1/${locale}/organisation/${catalogType?.brand_name}`,
                  )
                : null;
        // Поменять на AuthService.validateAuthSSR(cookies)
        if (isAuth) {
            const token = context?.req?.cookies?.accessToken;

            const {
                dto1: initialFavouritesResponse = null,
                dto2: initialComparesResponse = null,
                dto3: basketList = null,
            } = await QueryService.requests(
                `${process.env.NEXT_PUBLIC_DOMAIN_BFF}/tkmp/v1/${locale}/profile/favourite`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
                `${process.env.NEXT_PUBLIC_DOMAIN_BFF}/tkmp/v1/${locale}/profile/compare/item`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
                `${process.env.NEXT_PUBLIC_DOMAIN_BFF}/tkmp/v1/${locale}/profile/basket`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            return {
                props: {
                    pageData,
                    initialCatalogItems,
                    catalogType,
                    brandDto,
                    filterItems: filterItems?.resultSet,
                    catalogCategories,
                    breadcrumbs,
                    initialFavourites: initialFavouritesResponse,
                    initialBasketList: basketList?.items || [],
                    initialCompares: initialComparesResponse,
                    locale,
                },
            };
        }

        return {
            props: {
                pageData,
                initialCatalogItems,
                catalogType,
                brandDto,
                filterItems: filterItems?.resultSet,
                catalogCategories,
                breadcrumbs,
                initialBasketList: [],
                initialFavourites: [],
                initialCompares: [],
                locale,
            },
        };
    }

    return {
        props: {
            pageData,
            catalogType,
            initialCatalogItems: [],
            brandDto: [],
            filterItems: [],
            catalogCategories: [],
            breadcrumbs: [],
            initialBasketList: [],
            initialFavourites: [],
            initialCompares: [],
            locale,
        },
    };
}

// /catalog
// /catalog/[param_1]/.../[param_N]
// /catalog/[param_1]/.../[param_N]/brand
// /catalog/[param_1]/.../[param_N]/brand/[brand_name]
// /catalog/[param_1]/.../[param_N]/brand/[brand_name]/filter
// /catalog/[param_1]/.../[param_N]/brand/[brand_name]/filter/[filter_1]/.../[filter_N]
// /catalog/[param_1]/.../[param_N]/brand/[brand_name]/filter/[filter_1]/.../[filter_N]/apply

function CatalogExplorerPage({
    pageData,
    initialCatalogItems,
    catalogType,
    brandDto,
    filterItems,
    catalogCategories,
    breadcrumbs,
    initialFavourites,
    initialCompares,
    locale,
    initialBasketList,
}: any) {
    const router = useRouter();
    const [catalogItems, setCatalogItems] = useState<any>(initialCatalogItems);
    const LOCALIZATION = useLocalization();

    useEffect(() => {
        if (router?.query?.catalog_query) {
            const newCatalogType = CatalogQueryService.generateCatalogQuery(
                router?.query?.catalog_query,
            );

            QueryService.request(
                `${process.env.NEXT_PUBLIC_DOMAIN_BFF}/tkmp/v1/${locale}/catalog/product/home/${newCatalogType?.query_params?.join('/')}${CatalogQueryService.getFilterUrl(newCatalogType)}`,
            ).then((response) => {
                if (response) {
                    const temp = response;
                    temp.resultSet = temp.resultSet.map((item: any) => ({
                        ...item,
                        id: item.tid,
                    }));
                    setCatalogItems(temp);
                } else if (process.env.NEXT_PUBLIC_DEBUG === 'true') {
                }
            });
        }
    }, [router]);

    const {
        systemStore: { BREADCRUMBS, setBreadcrumbs },
    } = useStores();

    useEffect(() => {
        const baseBreadcrumbs = [
            {
                key: '/',
                title: LOCALIZATION.routes.home,
            },
            {
                key: '/catalog',
                title: LOCALIZATION.routes.catalog,
            },
        ];

        const dynamicBreadcrumbs = breadcrumbs.slice(2);

        setBreadcrumbs([...baseBreadcrumbs, ...dynamicBreadcrumbs]);
    }, [LOCALIZATION.locale, breadcrumbs]);

    // @ts-ignore
    return (
        catalogType?.query_params.length > 0 && (
            <>
                {process.env.NEXT_PUBLIC_INCLUDE_SEO_TAGS === 'true' &&
                    HeadService.simpleBuild('Каталог товаров', router.pathname, [
                        {
                            key: 'description',
                            value: pageData?.alias,
                        },
                        {
                            key: 'og:image',
                            value: pageData?.img_url
                                ? `${process.env.NEXT_PUBLIC_DOMAIN_CDN}${pageData?.img_url}`
                                : '',
                        },
                        {
                            key: 'twitter:image',
                            value: pageData?.logo
                                ? `${process.env.NEXT_PUBLIC_DOMAIN_CDN}${pageData?.logo}`
                                : '',
                        },
                    ])}

                <Flex
                    className={cep.body}
                    vertical
                    gap={'24px'}
                >
                    <Typography.Text className={`text-32-500`}>
                        {/* Доработать отображение названия раздела в каталоге. Старый код:*/}
                        {/* {BREADCRUMBS[BREADCRUMBS.length - 1]?.title ?? 'Каталог'}{' '} */}
                        {LOCALIZATION.pages.catalog.header.title}{' '}
                        {catalogType?.isBrand && brandDto?.fullname}{' '}
                        <span className={`text-14-400 text-[#7b7c7d] ${cep.title}`}>
                            {catalogItems?.count} {LOCALIZATION.pages.catalog.header.products}
                        </span>
                    </Typography.Text>
                    {catalogType?.isBrand && brandDto && <CatalogBrandHeader brand={brandDto} />}
                    {catalogType?.query_params.length > 0 && (
                        <CatalogExplorerCategory
                            isBrand={catalogType?.isBrand}
                            brandname={catalogType.brand_name}
                            categories={catalogCategories}
                        />
                    )}
                    <Flex
                        gap={'24px'}
                        style={{ flexGrow: '1' }}
                    >
                        <CatalogFiltersBar
                            catalogType={catalogType}
                            filterItems={filterItems}
                        />
                        <Flex
                            vertical
                            className={cep.body__dataContainer}
                        >
                            <CatalogActionsHeader />
                            {catalogType?.filter_params?.length > 0 && (
                                <CatalogFiltersTags
                                    catalogType={catalogType}
                                    filterItems={filterItems}
                                />
                            )}
                            <CatalogDisplay
                                items={catalogItems}
                                favourites={initialFavourites}
                                compares={initialCompares}
                                basketItems={initialBasketList}
                                cid={pageData?.cid}
                            />
                        </Flex>
                    </Flex>
                </Flex>
            </>
        )
    );
}

export default WithMainContainer(CatalogExplorerPage);
